import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// Get full image URL helper function
const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }
    
    // If starts with /uploads, use relative path (same domain)
    if (imagePath.startsWith('/uploads/')) {
        return imagePath; // Relative path works in production (same domain)
    }
    
    // Otherwise return as is (might be a data URL or other format)
    return imagePath;
};

const BannerSlider = ({ banners }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [translateX, setTranslateX] = useState(0);
    const sliderRef = useRef(null);
    const autoPlayRef = useRef(null);

    // Memoize displayed banners to prevent unnecessary re-renders
    // Use banner IDs and durations as dependency to detect actual changes
    const bannerKey = useMemo(() => {
        if (!banners || banners.length === 0) return '';
        return banners.map(b => `${b.id}-${b.display_duration || 5}`).join('|');
    }, [banners]);
    
    const displayedBanners = useMemo(() => {
        if (banners && banners.length > 0) return banners;
        return [];
    }, [bannerKey]);
    
    // Reset index when banners change significantly
    useEffect(() => {
        if (displayedBanners.length > 0 && currentIndex >= displayedBanners.length) {
            setCurrentIndex(0);
        }
    }, [displayedBanners.length]);

    // Auto-play functionality - Her banner için kendi süresini kullan
    const stopAutoPlay = useCallback(() => {
        if (autoPlayRef.current) {
            clearTimeout(autoPlayRef.current);
            autoPlayRef.current = null;
        }
    }, []);

    // Banner değiştiğinde timeout'u yeniden başlat - Her banner için kendi süresini kullan
    useEffect(() => {
        if (displayedBanners.length <= 1) {
            stopAutoPlay();
            return;
        }
        
        // Önce mevcut timeout'u temizle
        stopAutoPlay();
        
        const currentBanner = displayedBanners[currentIndex];
        if (!currentBanner) {
            console.warn('⚠️ Banner bulunamadı, index:', currentIndex);
            return;
        }
        
        // display_duration field'ını kontrol et - backend'den gelen field adı
        // display_duration saniye cinsinden geliyor, null olabilir
        const durationSeconds = currentBanner.display_duration;
        const duration = (durationSeconds && durationSeconds > 0 ? durationSeconds : 5) * 1000;
        
        // Her banner için ayrı süre kullan - setTimeout kullanarak daha güvenilir
        autoPlayRef.current = setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % displayedBanners.length);
        }, duration);

        return () => {
            stopAutoPlay();
        };
    }, [currentIndex, displayedBanners.length, stopAutoPlay]);

    // Navigation functions - Anında değişim, takılma yok
    const goToPrevious = useCallback(() => {
        stopAutoPlay();
        setCurrentIndex((prev) => (prev - 1 + displayedBanners.length) % displayedBanners.length);
    }, [displayedBanners.length, stopAutoPlay]);

    const goToNext = useCallback(() => {
        stopAutoPlay();
        setCurrentIndex((prev) => (prev + 1) % displayedBanners.length);
    }, [displayedBanners.length, stopAutoPlay]);

    const goToIndex = useCallback((index) => {
        stopAutoPlay();
        setCurrentIndex(index);
    }, [stopAutoPlay]);

    // Touch/Mouse handlers for swipe
    const handleDragStart = useCallback((clientX) => {
        setIsDragging(true);
        setStartX(clientX);
        stopAutoPlay();
    }, [stopAutoPlay]);

    const handleDragMove = useCallback((clientX) => {
        if (!isDragging) return;
        const diff = clientX - startX;
        setTranslateX(diff);
    }, [isDragging, startX]);

    const handleDragEnd = useCallback(() => {
        if (!isDragging) return;
        
        setIsDragging(false);
        const threshold = 50; // minimum swipe distance
        
        if (translateX > threshold && currentIndex > 0) {
            goToPrevious();
        } else if (translateX < -threshold && currentIndex < displayedBanners.length - 1) {
            goToNext();
        } else if (translateX > threshold && currentIndex === 0) {
            // Loop to last
            setCurrentIndex(displayedBanners.length - 1);
        } else if (translateX < -threshold && currentIndex === displayedBanners.length - 1) {
            // Loop to first
            setCurrentIndex(0);
        }
        
        setTranslateX(0);
    }, [isDragging, translateX, currentIndex, displayedBanners.length, goToPrevious, goToNext]);

    // Touch event handlers
    const handleTouchStart = useCallback((e) => {
        handleDragStart(e.touches[0].clientX);
    }, [handleDragStart]);

    const handleTouchMove = useCallback((e) => {
        handleDragMove(e.touches[0].clientX);
    }, [handleDragMove]);

    const handleTouchEnd = useCallback(() => {
        handleDragEnd();
    }, [handleDragEnd]);

    // Mouse event handlers
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        handleDragStart(e.clientX);
    }, [handleDragStart]);

    const handleMouseMove = useCallback((e) => {
        handleDragMove(e.clientX);
    }, [handleDragMove]);

    const handleMouseUp = useCallback(() => {
        handleDragEnd();
    }, [handleDragEnd]);

    const handleMouseLeave = useCallback(() => {
        if (isDragging) {
            handleDragEnd();
        }
    }, [isDragging, handleDragEnd]);

    // Restart autoplay after interaction - Kaldırıldı, currentIndex değiştiğinde zaten başlatılıyor

    if (!displayedBanners || displayedBanners.length === 0) {
        return null;
    }

    const hasMultipleBanners = displayedBanners.length > 1;

    return (
        <div className="relative w-full h-64 md:h-96 overflow-hidden rounded-2xl mb-6 shadow-xl group bg-gray-900">
            {/* Slider Track */}
            <div
                ref={sliderRef}
                className="flex h-full"
                style={{
                    transform: `translateX(calc(-${currentIndex * 100}% + ${translateX}px))`,
                    transition: isDragging ? 'none' : 'transform 0.5s ease-out',
                    cursor: isDragging ? 'grabbing' : 'grab'
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            >
                {displayedBanners.map((banner, index) => {
                    const imageUrl = getImageUrl(banner?.media_url);
                    
                    return (
                        <div
                            key={banner.id || index}
                            className="relative w-full h-full flex-shrink-0"
                            style={{ minWidth: '100%' }}
                        >
                            {banner.media_type === 'video' ? (
                                <video
                                    src={imageUrl}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="w-full h-full object-cover select-none pointer-events-none"
                                    onError={(e) => {
                                        console.error('Video load error:', e.target.src);
                                    }}
                                />
                            ) : (
                                <img
                                    src={imageUrl}
                                    alt={banner.title || `Banner ${index + 1}`}
                                    width={768}
                                    height={512}
                                    className="w-full h-full object-cover select-none pointer-events-none"
                                    draggable={false}
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                    decoding="async"
                                    fetchPriority={index === 0 ? 'high' : 'auto'}
                                    onError={(e) => {
                                        console.error('Image load error:', e.target.src);
                                    }}
                                />
                            )}

                            {/* Overlay for better text visibility */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

                            {/* Banner Title/Description */}
                            {(banner.title || banner.description) && (
                                <div className="absolute bottom-20 left-0 right-0 px-6 text-white z-10 pointer-events-none">
                                    {banner.title && (
                                        <h3 className="text-xl md:text-3xl font-bold mb-2 drop-shadow-lg">
                                            {banner.title}
                                        </h3>
                                    )}
                                    {banner.description && (
                                        <p className="text-sm md:text-lg drop-shadow-lg line-clamp-2 opacity-90">
                                            {banner.description}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Link Overlay */}
                            {banner.link_url && !isDragging && (
                                <a
                                    href={banner.link_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="absolute inset-0 z-20"
                                    aria-label={banner.title || 'Banner link'}
                                    onClick={(e) => {
                                        if (Math.abs(translateX) > 5) {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Navigation Arrows - Always visible on desktop */}
            {hasMultipleBanners && (
                <>
                    {/* Previous Button */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            goToPrevious();
                        }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all transform hover:scale-110 z-30 opacity-70 md:opacity-100 group-hover:opacity-100"
                        aria-label="Önceki banner"
                    >
                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Next Button */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            goToNext();
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all transform hover:scale-110 z-30 opacity-70 md:opacity-100 group-hover:opacity-100"
                        aria-label="Sonraki banner"
                    >
                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </>
            )}

            {/* Progress Bar */}
            {hasMultipleBanners && (
                <div className="absolute top-0 left-0 right-0 z-30 flex gap-1 p-2">
                    {displayedBanners.map((_, index) => (
                        <div
                            key={index}
                            className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden cursor-pointer"
                            onClick={() => goToIndex(index)}
                        >
                            <div
                                className={`h-full bg-white rounded-full transition-all duration-300 ${
                                    index === currentIndex ? 'w-full' : index < currentIndex ? 'w-full' : 'w-0'
                                }`}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Banner Counter & Dots Indicator */}
            {hasMultipleBanners && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-30">
                    {/* Banner Counter */}
                    <div className="mb-3 text-center">
                        <span className="bg-black/60 text-white text-sm font-medium px-4 py-1.5 rounded-full backdrop-blur-sm shadow-lg">
                            {currentIndex + 1} / {displayedBanners.length}
                        </span>
                    </div>
                    
                    {/* Dots Indicator */}
                    <div className="flex space-x-2 justify-center">
                        {displayedBanners.map((_, index) => (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    goToIndex(index);
                                }}
                                className={`transition-all duration-300 rounded-full shadow-md ${
                                    index === currentIndex 
                                        ? 'w-10 h-3 bg-white' 
                                        : 'w-3 h-3 bg-white/50 hover:bg-white/75'
                                }`}
                                aria-label={`Banner ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Swipe Hint - shows briefly on mobile */}
            {hasMultipleBanners && (
                <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-30 md:hidden animate-pulse">
                    <div className="flex items-center gap-2 text-white/70 text-xs">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                        </svg>
                        <span>Kaydır</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BannerSlider;
